import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { AppButton, IconButton, cn } from "../ui";
import { TONE_CHIP, TONE_PILL, iconFor, toneOf } from "./tokens";

/**
 * One row of the Action Center drawer.
 *
 * ── THE ROW IS AN ARGUMENT, IN FOUR PARTS ───────────────────────────────────
 * A prompt is only worth its pixels if it survives the question "so what?".
 * Every row answers it in a fixed order, and the layout is that order:
 *
 *   the NUMBER     how much of it there is — the only part that gets scanned
 *   the TITLE      what it is, in the institute's own words, not the schema's
 *   the BODY       why it costs them something. This is the part that earns
 *                  the click, and it is the part a status badge cannot carry.
 *   the ACTION     one verb, one destination. A row with no verb is a
 *                  complaint, and people stop reading complaints.
 *
 * ── WHY THIS STACKS INSTEAD OF SITTING IN A LINE ────────────────────────────
 * The dashboard-panel version of this put the action button on the right of the
 * row, which works at 1100px and falls apart at 400. The drawer is 420px wide
 * on purpose — it has to survive beside the nav on a laptop — so the action
 * goes UNDER the copy. That also puts it at the end of the argument rather than
 * beside the beginning of it, which is where a call to action belongs.
 *
 * ⚠️ `count` is rendered whenever it is not null, and the test is `!= null`,
 * not truthiness. `0` is a value the server never sends for a workload row (a
 * zero-count signal is not pushed at all), but the trial countdown genuinely
 * reaches zero on its last day — which is the day it matters most.
 */
const SignalRow = ({ signal, onDismiss }) => {
  const navigate = useNavigate();
  const tone = toneOf(signal.tone);
  const Icon = iconFor(signal.icon);

  return (
    <li className="tw-flex tw-gap-3 tw-px-4 tw-py-3.5 tw-transition hover:tw-bg-surface-hover">
      <span
        className={cn(
          "tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm",
          TONE_CHIP[tone],
        )}
      >
        <Icon size={15} aria-hidden="true" />
      </span>

      <div className="tw-min-w-0 tw-flex-1">
        <div className="tw-flex tw-items-start tw-gap-2">
          <div className="tw-min-w-0 tw-flex-1">
            {signal.count != null && (
              <span
                className={cn(
                  "tw-mb-1 tw-inline-block tw-rounded-sm tw-px-1.5 tw-py-0.5 tw-text-[0.8125rem] tw-font-semibold tw-leading-none",
                  TONE_PILL[tone],
                )}
              >
                {signal.count}
                {signal.countLabel ? (
                  <span className="tw-ml-1 tw-text-[0.6875rem] tw-font-medium">
                    {signal.countLabel}
                  </span>
                ) : null}
              </span>
            )}

            <span className="tw-block tw-text-[0.8125rem] tw-font-semibold tw-leading-snug tw-text-content">
              {signal.title}
            </span>
          </div>

          {signal.dismissible !== false && (
            <IconButton
              label={`Dismiss: ${signal.title}`}
              icon={X}
              size="xs"
              className="tw-shrink-0"
              onClick={() => onDismiss(signal)}
            />
          )}
        </div>

        {signal.body && (
          <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-leading-relaxed tw-text-content-muted">
            {signal.body}
          </p>
        )}

        {signal.action && (
          <AppButton
            variant="outline"
            size="xs"
            className="tw-mt-2"
            onClick={() => navigate(signal.action.to)}
          >
            {signal.action.label}
            <ArrowRight size={12} aria-hidden="true" className="tw-ml-1.5" />
          </AppButton>
        )}
      </div>
    </li>
  );
};

export default SignalRow;
